import { z, ZodError, ZodIssue } from 'zod';
import {
  maxWorkoutDurationMinutes,
  setModes,
} from '@client/new/pages/workout/models/workout.domain';
import { LegacyWorkoutFormValues } from '@client/new/pages/workout/models/workoutEdit.model';
import { Control } from 'react-hook-form';
import { last } from 'lodash';

/**
 * Validates if Workout is ready to be published
 * @param {LegacyWorkoutFormValues} values
 * @param {Control<LegacyWorkoutFormValues>} control
 * @return {boolean}
 */
export const validatePublish = (
  values: LegacyWorkoutFormValues,
  control: Control<LegacyWorkoutFormValues>,
): boolean => {
  try {
    publishValidationSchema.parse(values);

    return true;
  } catch (error) {
    const { issues } = error as ZodError;

    reportIssues(issues, control);

    console.log({
      message: 'Pre-publish validation errors',
      values,
      errors: issues,
    });

    return false;
  }
};

/**
 * A draft may keep unfilled amounts. A published workout goes live on save,
 * so its sets follow the publish rules.
 */
export const validateSave = (
  values: LegacyWorkoutFormValues,
  control: Control<LegacyWorkoutFormValues>,
  published: boolean,
): boolean => {
  const schema = published ? publishSetSchema : draftSetSchema;
  const exercises: ExerciseWithSets[] = values.exercises ?? [];
  const issues = exercises.flatMap((e, i) =>
    setsWithPaths(e, ['exercises', i]).flatMap(({ set, path }) => {
      const result = schema.safeParse(set);
      return result.success
        ? []
        : (result as z.SafeParseError<unknown>).error.issues.map((issue) => ({
            ...issue,
            path: [...path, ...issue.path],
          }));
    }),
  );

  reportIssues(issues, control);

  return issues.length === 0;
};

const reportIssues = (
  issues: ZodIssue[],
  control: Control<LegacyWorkoutFormValues>,
) =>
  issues.forEach((issue) => {
    const path = last(issue.path) === 0 ? issue.path.slice(0, -1) : issue.path;
    control.setError(path.join('.') as keyof LegacyWorkoutFormValues, {
      message: issue.message,
    });
  });

type ExerciseWithSets = {
  type: number;
  sets?: unknown[];
  exercises?: ExerciseWithSets[];
};

const setsWithPaths = (
  exercise: ExerciseWithSets,
  path: (string | number)[],
): { set: unknown; path: (string | number)[] }[] => {
  if (exercise.type === 2)
    return (exercise.sets ?? []).map((set, i) => ({
      set,
      path: [...path, 'sets', i],
    }));
  if (exercise.type === 3)
    return (exercise.exercises ?? []).flatMap((child, i) =>
      setsWithPaths(child, [...path, 'exercises', i]),
    );
  return [];
};

const setSchema = z.object({
  reps: z.tuple([z.coerce.number(), z.string()]),
  mode: z.enum(setModes).optional(),
  repsMax: z.coerce.number().nullish(),
});

const refineRange = (set: z.infer<typeof setSchema>, ctx: z.RefinementCtx) => {
  if (
    set.mode === 'range' &&
    (set.repsMax == null || set.repsMax <= set.reps[0])
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['repsMax'],
      message: 'Upper bound must be greater than the amount',
    });
  }
};

const draftSetSchema = setSchema.superRefine(refineRange);

export const publishSetSchema = setSchema.superRefine((set, ctx) => {
  if ((set.mode ?? 'fixed') !== 'toFailure' && set.reps[0] < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reps', 0],
      message: 'Amount must be at least 1',
    });
  }
  refineRange(set, ctx);
});

const simpleExerciseSchema = z.object({
  type: z.literal(2),
  name: z.string().nonempty({ message: 'Title is required' }),
  sets: z.array(publishSetSchema).optional(),
});

const toMultipleSchemaBase = <T extends number>(type: T) =>
  z.object({
    type: z.literal(type),
    name: z.string().nonempty({ message: 'Title is required' }),
    duration: z.tuple([z.coerce.number().gte(1), z.string()]),
    exercises: z.array(simpleExerciseSchema),
  });

const tabataSchema = z.object({
  type: z.literal(10),
  // name: z.string().nonempty({ message: 'Title is required' }),
  // duration: z.tuple([z.coerce.number().gte(1), z.string()]),
  exercises: z.array(simpleExerciseSchema),
});
const hiitSchema = z.object({
  type: z.literal(11),
  // name: z.string().nonempty({ message: 'Title is required' }),
  // duration: z.tuple([z.coerce.number().gte(1), z.string()]),
  exercises: z.array(simpleExerciseSchema),
});
const circuitSchema = toMultipleSchemaBase(3);

const longFormVideoSchema = z.object({
  type: z.literal(4),
  name: z.string().nonempty({ message: 'Title is required' }),
});

const runningExerciseSchema = z.object({
  type: z.literal(100),
  sets: z
    .array(z.object({ time: z.coerce.number().gte(1) }))
    .nonempty({ message: 'One or more exercises required' }),
});

const publishValidationSchema = z.object({
  name: z.string().nonempty({ message: 'Title is required' }),
  duration: z.coerce
    .number({ invalid_type_error: 'Duration must be defined' })
    .gte(1)
    .lte(maxWorkoutDurationMinutes),
  description: z
    .string()
    .nonempty({ message: 'Description is required when publishing a workout' }),
  programId: z
    .number({
      invalid_type_error: 'Collection is required when publishing a workout',
    })
    .gte(0),
  exercises: z
    .array(
      z.discriminatedUnion('type', [
        simpleExerciseSchema,
        circuitSchema,
        longFormVideoSchema,
        runningExerciseSchema,
        tabataSchema,
        hiitSchema,
      ]),
    )
    .nonempty({ message: 'One or more exercises required' }),
});
